/**
 * Tag Editor State Service
 * Centralized state management for tag editor
 * Single source of truth for HTML content, parsed tags, and editing state
 */

import { Injectable } from '@angular/core'
import { BehaviorSubject } from 'rxjs'
import { TAG_EDITOR_STRINGS } from '../tag-editor.constants'
import { InlineEditingState, InsertedTagCustom } from '../tag-editor.model'

@Injectable({ providedIn: 'root' })
export class TagEditorStateService {
  private readonly htmlContent$: BehaviorSubject<string> = new BehaviorSubject<string>(TAG_EDITOR_STRINGS.EMPTY_STRING)
  private readonly parsedTags$: BehaviorSubject<ReadonlyArray<InsertedTagCustom>> = new BehaviorSubject<
    ReadonlyArray<InsertedTagCustom>
  >([])
  private readonly editingTag$: BehaviorSubject<InlineEditingState | null> =
    new BehaviorSubject<InlineEditingState | null>(null)
  private readonly isTagSelectorOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)
  private readonly isInsertingTag$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)

  private isLoadingContent: boolean = false
  private isEditorInitialized: boolean = false
  private savedRange: Range | null = null
  private pendingContent: string | null = null

  getEditingTag(): InlineEditingState | null {
    return this.editingTag$.getValue()
  }

  getLoadingContent(): boolean {
    return this.isLoadingContent
  }

  getEditorInitialized(): boolean {
    return this.isEditorInitialized
  }

  getSavedRange(): Range | null {
    return this.savedRange
  }

  getPendingContent(): string | null {
    return this.pendingContent
  }

  getTagSelectorOpen(): boolean {
    return this.isTagSelectorOpen$.getValue()
  }

  getInsertingTag(): boolean {
    return this.isInsertingTag$.getValue()
  }

  getTagSelectorOpen$(): BehaviorSubject<boolean> {
    return this.isTagSelectorOpen$
  }

  getInsertingTag$(): BehaviorSubject<boolean> {
    return this.isInsertingTag$
  }

  setHtmlContent(content: string): void {
    this.htmlContent$.next(content)
  }

  setParsedTags(tags: ReadonlyArray<InsertedTagCustom>): void {
    this.parsedTags$.next(tags)
  }

  setEditingTag(state: InlineEditingState | null): void {
    this.editingTag$.next(state)
  }

  setLoadingContent(loading: boolean): void {
    this.isLoadingContent = loading
  }

  setEditorInitialized(initialized: boolean): void {
    this.isEditorInitialized = initialized
  }

  setSavedRange(range: Range | null): void {
    this.savedRange = range
  }

  setPendingContent(content: string | null): void {
    this.pendingContent = content
  }

  setTagSelectorOpen(open: boolean): void {
    this.isTagSelectorOpen$.next(open)
  }

  setInsertingTag(inserting: boolean): void {
    this.isInsertingTag$.next(inserting)
  }

  clearState(): void {
    this.htmlContent$.next(TAG_EDITOR_STRINGS.EMPTY_STRING)
    this.parsedTags$.next([])
    this.editingTag$.next(null)
    this.isLoadingContent = false
    this.isEditorInitialized = false
    this.savedRange = null
    this.pendingContent = null
    this.isTagSelectorOpen$.next(false)
    this.isInsertingTag$.next(false)
  }
}
